import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { errorIcon, eyeIcon, eyeSlashIcon, trashIcon } from '@ld.consumer-portal/data-uri';
import { Environment, ENVIRONMENT, TodoItem } from '@ld.consumer-portal/models';
import { ImageCompressionService } from '@ld.consumer-portal/utils';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';
export interface UploadDocumentEvent {
  todo: TodoItem;
  files: File[];
  notes: string[];
}

@Component({
  selector: 'ld-upload-document-modal',
  imports: [BaseModalComponent, MatIconModule, MatProgressBarModule, MatProgressSpinnerModule, TranslocoDirective],
  templateUrl: './upload-document-modal.component.html',
  styleUrl: './upload-document-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadDocumentModalComponent {
  private readonly env = inject<Environment>(ENVIRONMENT);
  private readonly transloco = inject(TranslocoService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly imageCompression = inject(ImageCompressionService);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed = false;
  private compressionQueue: Promise<void> = Promise.resolve();

  protected readonly errorIcon = errorIcon;
  protected readonly eyeIcon = eyeIcon;
  protected readonly eyeSlashIcon = eyeSlashIcon;
  protected readonly trashIcon = trashIcon;

  readonly todo = input.required<TodoItem>();

  readonly isUploading = input<boolean>(false);

  readonly uploadProgress = input<number>(0);

  readonly errorMessage = input<string | null>(null);

  readonly imageCompressionMinMb = input(2);

  readonly imageCompressionMaxEdgePx = input(2400);

  readonly imageCompressionQuality = input(0.75);

  readonly upload = output<UploadDocumentEvent>();

  readonly cancel = output<void>();

  protected readonly selectedFiles = signal<File[]>([]);

  protected readonly originalFileMeta = signal<{ name: string; size: number }[]>([]);

  protected readonly fileNotes = signal<string[]>([]);

  protected readonly showEditNotes = signal<boolean[]>([]);
  protected readonly showPreview = signal<boolean[]>([]);
  protected readonly previewUrls = signal<(string | null)[]>([]);
  protected readonly safePreviewUrls = signal<(SafeResourceUrl | null)[]>([]);

  protected readonly compressingFiles = signal<boolean[]>([]);

  protected readonly isDragOver = signal(false);

  protected readonly maxFileSizeInMb = computed(() => this.env.appSettings.documents?.maxFileSizeInMb ?? 10);

  protected readonly allowedExtensions = computed(
    () => this.env.appSettings.documents?.allowedExtensions ?? ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'tif', 'tiff'],
  );

  protected readonly validationError = signal<string | null>(null);

  protected readonly isAnyCompressing = computed(() => this.compressingFiles().some(Boolean));

  protected readonly canUpload = computed(
    () => this.selectedFiles().length > 0 && !this.isUploading() && !this.isAnyCompressing(),
  );

  protected onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(Array.from(input.files));
    }
    input.value = '';
  }

  protected onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(true);
  }

  protected onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);
  }

  protected onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver.set(false);

    if (event.dataTransfer?.files) {
      this.addFiles(Array.from(event.dataTransfer.files));
    }
  }

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.previewUrls().forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    });
  }

  protected removeFile(index: number): void {
    const url = this.previewUrls()[index];
    if (url) URL.revokeObjectURL(url);

    const files = [...this.selectedFiles()];
    const originalMeta = [...this.originalFileMeta()];
    const notes = [...this.fileNotes()];
    const editNotes = [...this.showEditNotes()];
    const preview = [...this.showPreview()];
    const urls = [...this.previewUrls()];
    const safeUrls = [...this.safePreviewUrls()];
    const compressing = [...this.compressingFiles()];
    files.splice(index, 1);
    originalMeta.splice(index, 1);
    notes.splice(index, 1);
    editNotes.splice(index, 1);
    preview.splice(index, 1);
    urls.splice(index, 1);
    safeUrls.splice(index, 1);
    compressing.splice(index, 1);
    this.selectedFiles.set(files);
    this.originalFileMeta.set(originalMeta);
    this.fileNotes.set(notes);
    this.showEditNotes.set(editNotes);
    this.showPreview.set(preview);
    this.previewUrls.set(urls);
    this.safePreviewUrls.set(safeUrls);
    this.compressingFiles.set(compressing);
  }

  protected updateNote(index: number, note: string): void {
    const notes = [...this.fileNotes()];
    notes[index] = note;
    this.fileNotes.set(notes);
  }

  protected toggleNotesEditing(index: number): void {
    const editNotes = [...this.showEditNotes()];
    const preview = [...this.showPreview()];
    editNotes[index] = !editNotes[index];
    if (editNotes[index]) preview[index] = false;
    this.showEditNotes.set(editNotes);
    this.showPreview.set(preview);
  }

  protected togglePreview(index: number): void {
    const preview = [...this.showPreview()];
    const editNotes = [...this.showEditNotes()];
    preview[index] = !preview[index];
    if (preview[index]) editNotes[index] = false;

    if (preview[index] && !this.previewUrls()[index]) {
      const file = this.selectedFiles()[index];
      const url = URL.createObjectURL(file);
      const urls = [...this.previewUrls()];
      const safeUrls = [...this.safePreviewUrls()];
      urls[index] = url;
      safeUrls[index] = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.previewUrls.set(urls);
      this.safePreviewUrls.set(safeUrls);
    }

    this.showPreview.set(preview);
    this.showEditNotes.set(editNotes);
  }

  protected isPreviewable(file: File): boolean {
    return file.type.startsWith('image/') || file.type === 'application/pdf';
  }

  protected onUpload(): void {
    if (!this.canUpload()) return;

    this.upload.emit({
      todo: this.todo(),
      files: this.selectedFiles(),
      notes: this.fileNotes(),
    });
  }

  protected onCancel(): void {
    if (!this.isUploading()) {
      this.cancel.emit();
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isUploading()) {
      this.cancel.emit();
    }
  }

  protected formatFileSize(bytes: number): string {
    if (bytes === 0) return this.transloco.translate('commonZeroBytes');
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private addFiles(files: File[]): void {
    this.validationError.set(null);
    const currentFiles = [...this.selectedFiles()];
    const currentOriginalMeta = [...this.originalFileMeta()];
    const currentNotes = [...this.fileNotes()];
    const currentEditNotes = [...this.showEditNotes()];
    const currentPreview = [...this.showPreview()];
    const currentUrls = [...this.previewUrls()];
    const currentSafeUrls = [...this.safePreviewUrls()];
    const currentCompressing = [...this.compressingFiles()];
    const acceptedFiles: File[] = [];
    const maxSize = this.maxFileSizeInMb() * 1024 * 1024;
    const allowedExts = this.allowedExtensions().map((ext) => ext.toLowerCase());

    for (const file of files) {
      if (file.size > maxSize) {
        this.validationError.set(
          this.transloco.translate('validationFileTooLarge', { fileName: file.name, maxSize: this.maxFileSizeInMb() }),
        );
        continue;
      }

      const ext = file.name.split('.').pop()?.toLowerCase() || '';
      if (!allowedExts.includes(ext)) {
        this.validationError.set(
          this.transloco.translate('validationInvalidFileType', {
            fileName: file.name,
            allowedTypes: this.allowedExtensions().join(', '),
          }),
        );
        continue;
      }

      if (!currentOriginalMeta.some((meta) => meta.name === file.name && meta.size === file.size)) {
        currentFiles.push(file);
        currentOriginalMeta.push({ name: file.name, size: file.size });
        acceptedFiles.push(file);
        currentNotes.push('');
        currentEditNotes.push(false);
        currentPreview.push(false);
        currentUrls.push(null);
        currentSafeUrls.push(null);
        currentCompressing.push(false);
      }
    }

    this.selectedFiles.set(currentFiles);
    this.originalFileMeta.set(currentOriginalMeta);
    this.fileNotes.set(currentNotes);
    this.showEditNotes.set(currentEditNotes);
    this.showPreview.set(currentPreview);
    this.previewUrls.set(currentUrls);
    this.safePreviewUrls.set(currentSafeUrls);
    this.compressingFiles.set(currentCompressing);
    this.enqueueCompression(acceptedFiles);
  }

  private enqueueCompression(files: File[]): void {
    this.compressionQueue = this.compressionQueue.then(() => this.compressFiles(files));
  }

  private async compressFiles(files: File[]): Promise<void> {
    for (const file of files) {
      if (this.destroyed) return;

      const index = this.selectedFiles().indexOf(file);
      if (index === -1 || !this.imageCompression.shouldCompress(file, this.imageCompressionMinMb() * 1_000_000)) {
        continue;
      }

      this.compressingFiles.update((compressing) => {
        const next = [...compressing];
        next[index] = true;
        return next;
      });

      let compressedFile = file;
      try {
        const result = await this.imageCompression.compress(file, {
          maxEdgePx: this.imageCompressionMaxEdgePx(),
          quality: this.imageCompressionQuality(),
          minBytes: this.imageCompressionMinMb() * 1_000_000,
        });
        if (this.destroyed) return;

        const currentIndex = this.selectedFiles().indexOf(file);
        if (currentIndex === -1) continue;

        compressedFile = result;
        this.selectedFiles.update((files) => {
          if (files[currentIndex] !== file) return files;
          const next = [...files];
          next[currentIndex] = compressedFile;
          return next;
        });
      } catch {
        compressedFile = file;
      } finally {
        if (!this.destroyed) {
          this.compressingFiles.update((compressing) => {
            const currentIndex = this.selectedFiles().findIndex(
              (currentFile) => currentFile === file || currentFile === compressedFile,
            );
            if (currentIndex === -1) return compressing;
            const next = [...compressing];
            next[currentIndex] = false;
            return next;
          });
        }
      }
    }
  }
}
